import * as Sentry from '@sentry/react';
import React from 'react';

// Environment variables - you'll need to add these to your .env files
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_NODE_ENV || 'local';
const isDebugEnabled = ENVIRONMENT === 'local' && import.meta.env.VITE_SENTRY_DEBUG_ENABLED === 'true';

// Only initialize Sentry if DSN is provided
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Set sampling rate for performance monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Error sampling
    replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Integrations including console logging
    integrations: [
      // Send console.log, console.error, and console.warn calls as logs to Sentry
      Sentry.consoleLoggingIntegration({
        levels: ['log', 'error', 'warn', 'info']
      })
    ],

    // Enable structured logs
    _experiments: {
      enableLogs: true,
      beforeSendLog: log => (ENVIRONMENT === 'local' ? null : log)
    },

    // Configure what gets sent to Sentry
    beforeSend(event, hint) {
      // Don't send errors in local
      if (ENVIRONMENT === 'local') {
        console.log('Sentry event (local):', event);
        return null;
      }

      // Filter out certain errors
      const error = hint.originalException;
      if (error instanceof Error) {
        // Ignore network errors
        if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
          return null;
        }

        // Ignore specific error types
        if (error.name === 'ChunkLoadError' || error.name === 'Loading chunk failed') {
          return null;
        }
      }

      return event;
    },

    // Configure breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive data
      if (breadcrumb.category === 'http') {
        const url = breadcrumb.data?.url;
        if (url && (url.includes('/api/auth') || url.includes('/api/users'))) {
          return null;
        }
      }

      return breadcrumb;
    },

    // Debug mode - only in local and if explicitly enabled
    debug: isDebugEnabled,

    // Configure transport
    transport: ENVIRONMENT === 'local' ? undefined : undefined,

    // Configure error boundaries
    attachStacktrace: true,

    // Configure user context
    sendDefaultPii: false
  });
}

// Export Sentry for use in components
export { Sentry };

// Export error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export performance monitoring
export const SentryProfiler = Sentry.Profiler;

// Export transaction for custom performance monitoring
export const startTransaction = (name: string, operation?: string) => {
  // Placeholder for transaction tracking
  console.log(`Transaction: ${name}`, operation);
};

// Export capture methods
export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const setUser = Sentry.setUser;
export const setTag = Sentry.setTag;
export const setContext = Sentry.setContext;
export const addBreadcrumb = Sentry.addBreadcrumb;
export const logger = Sentry.logger;

// Custom error boundary component with better UX
export function CustomErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <SentryErrorBoundary
      fallback={({ error, componentStack, resetError }: any) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
              <p className="mt-2 text-sm text-gray-500">
                We've encountered an unexpected error. Our team has been notified.
              </p>
              <div className="mt-6 flex space-x-3">
                <button
                  onClick={resetError}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try again
                </button>
                <button
                  onClick={() => (window.location.href = '/')}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Go home
                </button>
              </div>
            </div>
            {import.meta.env.DEV && (
              <details className="mt-4 text-left">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                  Error details (local only)
                </summary>
                <pre className="mt-2 text-xs text-gray-500 bg-gray-100 p-2 rounded overflow-auto">
                  {error?.toString()}
                  {componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    >
      {children}
    </SentryErrorBoundary>
  );
}

// Utility function to set user context
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
  organization?: string;
}) {
  setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    ip_address: '{{auto}}'
  });

  if (user.organization) {
    setTag('organization', user.organization);
  }
}

// Utility function to track API errors
export function trackApiError(error: any, endpoint: string, method: string) {
  // Convert non-Error objects to proper Error instances
  const errorToCapture =
    error instanceof Error ? error : new Error(error?.message || `API Error: ${method} ${endpoint}`);

  captureException(errorToCapture, {
    tags: {
      type: 'api_error',
      endpoint,
      method
    },
    contexts: {
      api: {
        endpoint,
        method,
        status: error?.response?.status || error?.status,
        data: error?.response?.data || error?.data
      }
    }
  });
}
